import {
  GeoJSONFeature,
} from 'maplibre-gl';

import {geojsonTypes,modes,cursors} from './utils/constants';
import doubleClickZoom from './utils/double_click_zoom';
import DrawPolygon from './utils/draw_polygon';
import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './utils/CalculateSnapTolerance';

import {Source, drawState } from '../../Types';

const SnapFreehandPolygonMode = { ...DrawPolygon };

SnapFreehandPolygonMode.onSetup = function (options:any) {
  // this.map._snapFreehandPolygonMode = SnapFreehandPolygonMode;
  // try { 
  //   document.getElementsByClassName('geobox-gl-rotate-icon')[0].checked = false;
  //   document.getElementsByClassName('geobox-gl-hole-icon')[0].checked = false;
  //   document.getElementsByClassName('geobox-gl-split-icon')[0].checked = false;
  // } catch (error) {
  // }
  const polygon = this.newFeature({
    type: geojsonTypes.FEATURE,
    properties: {},
    geometry: {
      type: geojsonTypes.POLYGON,
      coordinates: [[]],
    },
  });

  this.addFeature(polygon);

  const selectedFeatures = this.getSelected();
  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);

  // Disable map pan so pointer-drag draws the lasso instead of panning the map.
  // Defer one tick: gl-draw mode transitions can re-enable dragPan briefly.
  const map = this.map;
  map.dragPan.disable();
  map.doubleClickZoom.disable();
  setTimeout(() => {
    if (!map?.dragPan) return;
    map.dragPan.disable();
    map.doubleClickZoom.disable();
  }, 0);

  const state:drawState =  <drawState>{
    map: this.map,
    polygon,
    currentVertexPosition: 0,
    selectedFeatures,
    dragMoving: false,
    keepVisibleFeatureIds: Array.isArray(options?.keepVisibleFeatureIds)
      ? options.keepVisibleFeatureIds.map(String)
      : [],
  };
  state.options = sanitizeSnapOptions(this._ctx.options);

  const optionsChangedCallBAck = (options:drawState["options"]) => {
    state.options = sanitizeSnapOptions(options);
  };

  state.optionsChangedCallBAck = optionsChangedCallBAck;

  this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

  this.snapMarkerSource = (<Source>this.map.getSource('geobox-gl-snap-marker'))._data;
  SnapFreehandPolygonMode.state = state;

  this.updateUIClasses({ mouse: cursors.ADD });

  return state;
};


SnapFreehandPolygonMode.onDrag = SnapFreehandPolygonMode.onTouchMove = function (state:drawState, e:any){
  // Pure freehand lasso: stream vertices while the pointer is held and moved.
  state.dragMoving = true;
  this.map.getCanvasContainer().style.cursor = "crosshair";
  this.updateUIClasses({ mouse: cursors.ADD });

  const { lng, lat } = e.lngLat;
  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;
  
  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    state.snappedLng = desiredCoord[0];
    state.snappedLat = desiredCoord[1];
  } else {
    state.snappedLng = lng;
    state.snappedLat = lat;
  }

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, state.snappedLng, state.snappedLat, {
      locked,
      kind: locked ? snapKind ?? null : null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }
  
  state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, state.snappedLng, state.snappedLat);
  state.currentVertexPosition++;
  state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, state.snappedLng, state.snappedLat);
}

/** Finish the lasso on pointer release after a drag (not click-to-vertex like polygon). */
SnapFreehandPolygonMode.onMouseUp = SnapFreehandPolygonMode.onTouchEnd = function (state:drawState) {
  if (!state.dragMoving) return;

  const featureId = state.polygon?.id;
  if (featureId == null) {
    return this.changeMode(modes.SIMPLE_SELECT);
  }
  return this.changeMode(modes.SIMPLE_SELECT, {
    featureIds: [String(featureId)],
  });
};

/** Ignore clicks/taps so freehand never falls back to polygon vertex placement. */
SnapFreehandPolygonMode.onTap = SnapFreehandPolygonMode.onClick = function () {
  // no-op
};

SnapFreehandPolygonMode.onMouseMove = function (state:drawState, e:any) {
  const { lng, lat } = e.lngLat;
  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;

  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    state.snappedLng = desiredCoord[0];
    state.snappedLat = desiredCoord[1];
  } else {
    state.snappedLng = lng;
    state.snappedLat = lat;
  }

  (state as any)._snapLocked = locked;
  (state as any)._snapKind = locked ? snapKind : null;

  // if (state.options.limitDrawLength && state.options.limitDrawBearing){

  //   if (state.polygon.coordinates[0].length >= 2){
  //     var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //     var limitDrawBearing = document.getElementById('geobox-gl-geometry-bearing-parameter');

  //     if (limitDrawLength){
  //       const pXY0 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-1}`);
  //       const pXY0_3857 = deegrees2meters(pXY0);
  //       const pXY2_3857 = [];
  //       if (Number(limitDrawBearing.value) === 90){
  //         pXY2_3857[0] = pXY0_3857[0];
  //         pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value);
  //       }else if (Number(limitDrawBearing.value) === 270){
  //         pXY2_3857[0] = pXY0_3857[0];
  //         pXY2_3857[1] = pXY0_3857[1]- Number(limitDrawLength.value);
  //       }else {
  //         pXY2_3857[0] = pXY0_3857[0]+ Number(limitDrawLength.value)*Math.cos(Number(limitDrawBearing.value)*Math.PI/180);
  //         pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value)*Math.sin(Number(limitDrawBearing.value)*Math.PI/180);
  //       }

  //       const pXY2 = meters2degrees(pXY2_3857);
        
  //       state.snappedLng = pXY2[0];
  //       state.snappedLat = pXY2[1];
  //     }
  //   }
  // } else if (state.options.limitDrawLength && state.options.limitDrawAngle){

  //   if (state.polygon.coordinates[0].length >= 3){
  //     var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //     var limitDrawAngle = document.getElementById('geobox-gl-geometry-angle-parameter');

  //     if (limitDrawLength){
  //       const pXY0 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-2}`);
  //       const pXY0_3857 = deegrees2meters(pXY0);
  //       const pXY1 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-1}`);
  //       const pXY1_3857 = deegrees2meters(pXY1);

  //       // const pXY2_3857 = deegrees2meters([lng, lat]);

  //       const pXY3_3857 = [];

  //       var angle = lineAngle(pXY0_3857, pXY1_3857);

  //       var newAngle = 180 - (Number(limitDrawAngle.value) - angle );

  //       if (newAngle === 90){
  //         pXY3_3857[0] = pXY1_3857[0];
  //         pXY3_3857[1] = pXY1_3857[1]+ Number(limitDrawLength.value);
  //       }else if (newAngle === 270){
  //         pXY3_3857[0] = pXY1_3857[0];
  //         pXY3_3857[1] = pXY1_3857[1]- Number(limitDrawLength.value);
  //       }else {
  //         pXY3_3857[0] = pXY1_3857[0]+ Number(limitDrawLength.value)*Math.cos(newAngle*Math.PI/180);
  //         pXY3_3857[1] = pXY1_3857[1]+ Number(limitDrawLength.value)*Math.sin(newAngle*Math.PI/180);
  //       }

  //       const pXY3 = meters2degrees(pXY3_3857);
      
  //       state.snappedLng = pXY3[0];
  //       state.snappedLat = pXY3[1];
  //     }
  //   }
  // }else {
  //   if (state.options.limitDrawLength) {
  //     if (state.polygon.coordinates[0].length >= 2){
  //       var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //       if (limitDrawLength){
  //         const pXY0 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-1}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1_3857 = deegrees2meters([lng, lat]);
  //         const pXY2_3857 = [];
         
  //         var dy = pXY1_3857[1]-pXY0_3857[1];
  //         var dx = pXY1_3857[0]-pXY0_3857[0];
  
  //         if (dx >= 0 ){
  //           pXY2_3857[0] = pXY0_3857[0]+ Number(limitDrawLength.value)*Math.cos(Math.atan(dy/dx));
  //           pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value)*Math.sin(Math.atan(dy/dx));
  //         }else {
  //           pXY2_3857[0] = pXY0_3857[0]- Number(limitDrawLength.value)*Math.cos(Math.atan(dy/dx));
  //           pXY2_3857[1] = pXY0_3857[1]- Number(limitDrawLength.value)*Math.sin(Math.atan(dy/dx));
  //         }
  
  //         const pXY2 = meters2degrees(pXY2_3857);
        
  //         state.snappedLng = pXY2[0];
  //         state.snappedLat = pXY2[1];
  //       }
  //     }
  //   }
  
  //   if (state.options.limitDrawBearing) {
  //     if (state.polygon.coordinates[0].length >= 2){
  //       var limitDrawBearing = document.getElementById('geobox-gl-geometry-bearing-parameter');
  //       if (limitDrawBearing){
  //         const pXY0 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-1}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1_3857 = deegrees2meters([lng, lat]);
  //         const pXY2_3857 = [];

  //         if (Number(limitDrawBearing.value) !== 90 && Number(limitDrawBearing.value) !== 270){
  //           pXY2_3857[0] = pXY1_3857[0];
  //           pXY2_3857[1] = pXY0_3857[1]+ Math.tan(Number(limitDrawBearing.value)*Math.PI/180)*(pXY2_3857[0]-pXY0_3857[0]);
  //         }else {
  //           pXY2_3857[0] = pXY0_3857[0];
  //           pXY2_3857[1] = pXY1_3857[1];
  //         }
         
  //         const pXY2 = meters2degrees(pXY2_3857);
        
  //         state.snappedLng = pXY2[0];
  //         state.snappedLat = pXY2[1];
  //       }
  //     }
  //   }

  //   if (state.options.limitDrawAngle) {
  //     if (state.polygon.coordinates[0].length >= 3){
  //       var limitDrawAngle = document.getElementById('geobox-gl-geometry-angle-parameter');
  //       if (limitDrawAngle){
        
  //         const pXY0 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-2}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1 = state.polygon.getCoordinate(`0.${state.currentVertexPosition-1}`);
  //         const pXY1_3857 = deegrees2meters(pXY1);

  //         const pXY2_3857 = deegrees2meters([lng, lat]);

  //         const pXY3_3857 = [];

  //         var angle = lineAngle(pXY0_3857, pXY1_3857);

        
  //         var newAngle = 180 - (Number(limitDrawAngle.value) - angle );

  //         if (newAngle !== 90 && newAngle !== 270){
  //           pXY3_3857[0] = pXY2_3857[0];
  //           pXY3_3857[1] = pXY1_3857[1]+ Math.tan(newAngle*Math.PI/180)*(pXY3_3857[0]-pXY1_3857[0]);
  //         }else {
  //           pXY3_3857[0] = pXY0_3857[0];
  //           pXY3_3857[1] = pXY2_3857[1];
  //         }
          
  //         const pXY3 = meters2degrees(pXY3_3857);
        
  //         state.snappedLng = pXY3[0];
  //         state.snappedLat = pXY3[1];
  //       }
  //     }
  //   }
  // }

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, state.snappedLng, state.snappedLat, {
      locked: Boolean((state as any)._snapLocked),
      kind: (state as any)._snapKind ?? null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }

  state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, state.snappedLng, state.snappedLat);
  this.updateUIClasses({ mouse: cursors.ADD });
};

SnapFreehandPolygonMode.toDisplayFeatures = function (state:drawState, geojson:GeoJSONFeature, display:any) {
  DrawPolygon.toDisplayFeatures(state, geojson, display);
};

// This is 'extending' DrawPolygon.onStop
SnapFreehandPolygonMode.onStop = function (state:drawState) {
  SnapFreehandPolygonMode.state = <drawState>{};

  this.snapMarkerSource.features = [{
    "type": "Feature",
    "geometry": {
      "type": "Point",
      "coordinates": []
    }
  }];

  (<Source>this.map.getSource('geobox-gl-snap-marker')).setData(this.snapMarkerSource);

  this.map.getCanvasContainer().style.cursor = "grab";
  this.map.off('moveend', <any>state.moveendCallback);
  this.map.off('draw.snap.options_changed', <any>state.optionsChangedCallBAck);
  DrawPolygon.onStop.call(this, state);

  // Re-enable map pan after freehand (disabled in onSetup so drag draws instead of pans).
  setTimeout(() => {
    try {
      if (this.map?.dragPan) this.map.dragPan.enable();
    } catch {
      /* ignore */
    }
  }, 0);
};

export default SnapFreehandPolygonMode;
